<template>
    <div class="deletion-request-modal">
        <ModalPattern class="pattern" />

        <div class="header">
            <div class="icon-container">
            <div class="company-logo" :class="companyData?.logo ? 'relative -top-2 -left-2' : 'without-logo'">
                <img v-if="companyData?.logo" :src="companyData.logo" :alt="companyData.name" class="w-12 h-12 rounded-full" />
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="#EF4444"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </div>
            </div>

            <div class="title">
                <h4>Delete My Data From {{ companyData?.name }}</h4>
                <p>We haven't tracked any exposures from this company, but it is in our database. Do you believe they hold your data—for example, through targeted emails? If so, would you like us to initiate a custom removal request on your behalf?</p>
            </div>
        </div>

        <div class="actions-section">
            <prime-button variant="secondary" @click="closeDialog" class="p-btn-root_secondary">Cancel</prime-button>
            <prime-button 
                label="Confirm" 
                @click="handleDeletionRequest"
                :loading="isRequesting"
                :disabled="isRequesting"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import ModalPattern from '~/assets/images/illustrations/modal-pattern.svg';
import { exposuresService } from '~/services/exposures-service';
import { useExposuresStore } from '~/stores/person/exposures';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.DELETION_REQUEST);
const companyData = computed(() => dialogRef.value?.data?.company);

const isRequesting = ref(false);
const store = useExposuresStore();
const { showSuccess, showError } = useGlobalToast();

const closeDialog = () => {
    dialogRef.value.close();
};

const handleDeletionRequest = async () => {
    if (!companyData.value?.id) {
        showError('Error', 'No company selected');
        return;
    }

    // If licensed, make API call through exposures service
    isRequesting.value = true;

    try {
        await exposuresService.requestDeletion(companyData.value.id);
        showSuccess('Success', 'Deletion request sent successfully');
        
        // Refresh the exposures store to show the new company in the table
        await store.fetchDetails();
        
        closeDialog();
    } catch (error: any) {
        showError('Error', error.message || 'Failed to send deletion request. Please try again.');
    } finally {
        isRequesting.value = false;
    }
};
</script>

<style lang="scss" scoped>
.deletion-request-modal {
    @apply p-6 sm:max-w-lg sm:w-[544px] w-11/12 rounded-lg bg-white mx-auto h-auto overflow-y-auto relative;

    .pattern {
        @apply absolute left-0 top-0 z-[0];
    }
}
.header {
    @apply relative z-10 flex flex-row gap-2 justify-start items-start pb-8;

    .icon-container {
        @apply flex justify-center items-center flex-col gap-8 relative p-2 rounded-full relative;
    }
    .title {
        @apply flex-1;

        h4 {
            @apply text-gray-900 font-inter text-lg font-semibold leading-7 mb-1;
        }

        p {
            @apply text-gray-600 font-inter text-sm font-normal leading-5;
        }
    }

    .close-icon {
        @apply cursor-pointer p-1 rounded-full hover:bg-gray-100 transition-colors;
    }
}

.company-info {
    @apply flex items-center gap-3 p-4 rounded-lg bg-gray-50 my-6 relative;

    .company-logo {
        @apply w-12 h-12 rounded-full bg-slate-200 flex-shrink-0;
    }

    .company-details {
        @apply flex flex-col gap-1;

        h5 {
            @apply text-gray-900 font-inter text-sm font-medium leading-normal;
        }

        span {
            @apply text-gray-600 font-inter text-sm font-normal leading-normal;
        }
    }
}

.actions-section {
    @apply flex gap-3 justify-end;
}
.without-logo {
    @apply border-8 border-error-50 bg-error-200 rounded-full -top-4 right-4 relative;
}
</style> 